// src/components/CMDashboard.jsx — GoldenScope Contract Manager Dashboard
import { useState, useEffect } from 'react';
import supabase from '../utils/supabaseClient';
import SOWDraftingModule from './SOWDraftingModule';

const STATUS_META = {
  drafting:   { label: 'In Progress',  color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
  reviewing:  { label: 'In Review',    color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
  complete:   { label: 'Complete',     color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
  submitted:  { label: 'Submitted',    color: '#c49922', bg: '#fffbeb', border: '#fde68a' },
};

const SPYGLASS_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="100%" height="100%"><rect x="2" y="13" width="18" height="6" rx="3" fill="url(#ab1)"/><rect x="17" y="14" width="7" height="4" rx="2" fill="url(#ab2)"/><rect x="22" y="14.5" width="7" height="3" rx="1.5" fill="url(#ab3)"/><circle cx="4" cy="16" r="3.5" fill="none" stroke="#2A1200" stroke-width="1.5"/><circle cx="4" cy="16" r="2" fill="url(#alens)" opacity="0.85"/><rect x="8" y="13" width="1.5" height="6" rx="0.5" fill="url(#aband)"/><rect x="14" y="13" width="1.5" height="6" rx="0.5" fill="url(#aband)"/><rect x="19" y="14" width="1" height="4" rx="0.4" fill="url(#aband)"/><line x1="24" y1="14.8" x2="24" y2="17.2" stroke="#1A0A00" stroke-width="0.6" opacity="0.5"/><line x1="25.5" y1="14.8" x2="25.5" y2="17.2" stroke="#1A0A00" stroke-width="0.6" opacity="0.5"/><defs><linearGradient id="ab1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#6B3A00"/><stop offset="40%" stop-color="#3D2000"/><stop offset="100%" stop-color="#1A0A00"/></linearGradient><linearGradient id="ab2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#5A3000"/><stop offset="50%" stop-color="#3A1A00"/><stop offset="100%" stop-color="#1A0800"/></linearGradient><linearGradient id="ab3" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#4A2800"/><stop offset="100%" stop-color="#150700"/></linearGradient><linearGradient id="aband" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#7A4500"/><stop offset="100%" stop-color="#2A1000"/></linearGradient><radialGradient id="alens" cx="35%" cy="30%"><stop offset="0%" stop-color="#4A7AB5"/><stop offset="100%" stop-color="#0A1F3D"/></radialGradient></defs></svg>';


export default function CMDashboard({ profile, cmProfile, apiKey, callAI, onSignOut, agencyLogoUrl }) {
  const [view, setView]               = useState('list'); // 'list' | 'drafting'
  const [activeDraft, setActiveDraft] = useState(null);
  const [drafts, setDrafts]           = useState([]);
  const [loading, setLoading]         = useState(true);
  const [deleting, setDeleting]       = useState(null);
  const [pennyTip, setPennyTip]       = useState('');
  const [tipLoading, setTipLoading]   = useState(false);

  useEffect(() => { loadDrafts(); }, []);

  async function loadDrafts() {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data, error } = await supabase
        .from('sow_drafts').select('*').eq('cm_user_id', user.id)
        .order('updated_at', { ascending: false });
      if (!error) setDrafts(data || []);
    } catch (e) { console.error('loadDrafts:', e); }
    finally { setLoading(false); }
  }

  async function deleteDraft(id) {
    if (!window.confirm('Delete this SOW draft? This cannot be undone.')) return;
    setDeleting(id);
    try {
      await supabase.from('sow_drafts').delete().eq('id', id);
      setDrafts(p => p.filter(d => d.id !== id));
    } catch (e) { console.error('deleteDraft:', e); }
    finally { setDeleting(null); }
  }

  async function loadPennyTip() {
    if (!apiKey || tipLoading) return;
    setTipLoading(true);
    try {
      const tip = await callAI(apiKey, {
        system: 'You are Penny, an AI assistant for California state Contract Managers. Give a single short, practical tip (2-3 sentences max) about writing a strong SOW or navigating California state contracting. Reference a specific SCM section if relevant. Be encouraging and concise.',
        messages: [{ role: 'user', content: `I have ${drafts.length} SOW draft(s). Give me a quick tip for today.` }],
        max_tokens: 150,
      });
      setPennyTip(tip);
    } catch {
      setPennyTip("A well-defined Scope of Work is the foundation of every successful contract. The clearer your Five W's, the smoother the contracting process will be.");
    } finally { setTipLoading(false); }
  }

  useEffect(() => { if (!loading && apiKey) loadPennyTip(); }, [loading]); // eslint-disable-line

  function openDraft(draft) { setActiveDraft(draft); setView('drafting'); }
  function startNewSOW()    { setActiveDraft(null);  setView('drafting'); }

  function handleDraftSaved(updated) {
    setActiveDraft(updated);
    setDrafts(p => {
      const exists = p.find(d => d.id === updated.id);
      return exists ? p.map(d => d.id === updated.id ? updated : d) : [updated, ...p];
    });
  }

  // Route to SOW drafting module
  if (view === 'drafting') {
    return (
      <SOWDraftingModule
        draft={activeDraft}
        profile={profile}
        cmProfile={cmProfile}
        apiKey={apiKey}
        callAI={callAI}
        onBack={() => { setView('list'); loadDrafts(); }}
        onDraftSaved={handleDraftSaved}
      />
    );
  }

  const stats = {
    total:     drafts.length,
    drafting:  drafts.filter(d => d.status === 'drafting').length,
    complete:  drafts.filter(d => d.status === 'complete').length,
    submitted: drafts.filter(d => d.status === 'submitted').length,
  };

  function fmtDate(iso) {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  function fmtRelative(iso) {
    if (!iso) return '';
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (mins < 2)   return 'just now';
    if (mins < 60)  return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7)   return `${days}d ago`;
    return fmtDate(iso);
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: 'Inter, system-ui, sans-serif' }}>

      {/* Nav */}
      <nav style={{ background: '#0f1f3d', padding: '0 28px', height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 2px 12px rgba(0,0,0,.25)', position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {agencyLogoUrl ? (
            <img src={agencyLogoUrl} alt={profile?.agencyName || 'Agency'} style={{ width: 32, height: 32, borderRadius: 8, objectFit: 'contain', background: '#fff', padding: 3, border: '1px solid rgba(255,255,255,.2)' }} />
          ) : (
            <div style={{ width: 32, height: 32, borderRadius: 8, background: 'linear-gradient(135deg,#c49922,#e8c04a)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 4 }} dangerouslySetInnerHTML={{ __html: SPYGLASS_SVG }} />
          )}
          <div>
            <div style={{ color: '#fff', fontSize: 14, fontWeight: 700, lineHeight: 1.2 }}>GoldenScope</div>
            <div style={{ color: 'rgba(255,255,255,.4)', fontSize: 10 }}>Contract Manager Portal</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>{cmProfile?.name || profile?.contactName || 'Contract Manager'}</div>
            <div style={{ color: 'rgba(255,255,255,.4)', fontSize: 11 }}>
              {cmProfile?.agency_name || profile?.agencyName || ''}{cmProfile?.division ? ' · ' + cmProfile.division : ''}
            </div>
          </div>
          <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'linear-gradient(135deg,#c49922,#e8c04a)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 800, color: '#0f1f3d' }}>
            {(cmProfile?.name || profile?.contactName || 'C')[0].toUpperCase()}
          </div>
          <button onClick={onSignOut} style={{ padding: '6px 14px', borderRadius: 7, border: '1px solid rgba(255,255,255,.15)', background: 'rgba(255,255,255,.07)', color: 'rgba(255,255,255,.6)', fontSize: 12, cursor: 'pointer' }}>Sign Out</button>
        </div>
      </nav>

      <div style={{ maxWidth: 900, margin: '0 auto', padding: '32px 24px' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
          <div>
            <h1 style={{ fontSize: 24, fontWeight: 800, color: '#0f1f3d', margin: 0 }}>My SOW Drafts</h1>
            <p style={{ fontSize: 13.5, color: '#64748b', marginTop: 4 }}>Draft and refine your Statements of Work with Penny before submitting a contract request.</p>
          </div>
          <button onClick={startNewSOW} style={{ padding: '11px 22px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#c49922,#e8c04a)', color: '#0f1f3d', fontWeight: 700, fontSize: 14, cursor: 'pointer', boxShadow: '0 4px 14px rgba(196,153,34,.35)', display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
            ✒ Start New SOW
          </button>
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 28 }}>
          {[
            { label: 'Total Drafts', value: stats.total,     icon: '📄', color: '#0f1f3d' },
            { label: 'In Progress',  value: stats.drafting,  icon: '✏️',  color: '#2563eb' },
            { label: 'Complete',     value: stats.complete,  icon: '✅', color: '#16a34a' },
            { label: 'Submitted',    value: stats.submitted, icon: '📬', color: '#c49922' },
          ].map(s => (
            <div key={s.label} style={{ background: '#fff', borderRadius: 12, padding: '16px 18px', border: '1.5px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,.04)' }}>
              <div style={{ fontSize: 20, marginBottom: 6 }}>{s.icon}</div>
              <div style={{ fontSize: 26, fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.value}</div>
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4, fontWeight: 500 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Penny tip */}
        <div style={{ background: '#0f1f3d', borderRadius: 12, padding: '16px 20px', marginBottom: 28, display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <div style={{ width: 36, height: 36, borderRadius: '50%', flexShrink: 0, background: 'linear-gradient(135deg,#c49922,#e8c04a)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 900, color: '#0f1f3d' }}>P</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#c49922', marginBottom: 4, letterSpacing: '.04em' }}>PENNY'S TIP OF THE DAY</div>
            {tipLoading ? (
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                {[0,1,2].map(i => <div key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: 'rgba(255,255,255,.3)', animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite` }} />)}
              </div>
            ) : (
              <div style={{ fontSize: 13.5, color: 'rgba(255,255,255,.8)', lineHeight: 1.7 }}>
                {pennyTip || "A well-defined Scope of Work is the foundation of every successful contract."}
              </div>
            )}
          </div>
          <button onClick={loadPennyTip} disabled={tipLoading} style={{ background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.12)', borderRadius: 6, color: 'rgba(255,255,255,.5)', fontSize: 11, padding: '4px 10px', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
            {tipLoading ? '…' : '↻ New tip'}
          </button>
        </div>

        {/* Draft list */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '48px 0' }}>
            <div style={{ width: 36, height: 36, borderRadius: '50%', margin: '0 auto 12px', border: '3px solid #e2e8f0', borderTopColor: '#c49922', animation: 'spin 0.8s linear infinite' }} />
            <div style={{ color: '#94a3b8', fontSize: 13 }}>Loading your drafts…</div>
          </div>
        ) : drafts.length === 0 ? (
          <div style={{ background: '#fff', borderRadius: 16, border: '2px dashed #e2e8f0', padding: '56px 32px', textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>✒</div>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: '#0f1f3d', marginBottom: 8 }}>No SOW drafts yet</h3>
            <p style={{ fontSize: 13.5, color: '#64748b', lineHeight: 1.7, maxWidth: 420, margin: '0 auto 24px' }}>
              When you have an idea for a service your department needs, start here. Penny will guide you through the Five W's to build a compliant Exhibit A SOW.
            </p>
            <button onClick={startNewSOW} style={{ padding: '12px 28px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#c49922,#e8c04a)', color: '#0f1f3d', fontWeight: 700, fontSize: 14, cursor: 'pointer', boxShadow: '0 4px 14px rgba(196,153,34,.3)' }}>
              ✒ Start Your First SOW
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {drafts.map(draft => {
              const meta = STATUS_META[draft.status] || STATUS_META.drafting;
              return (
                <div key={draft.id}
                  onClick={() => openDraft(draft)}
                  style={{ background: '#fff', borderRadius: 12, border: '1.5px solid #e2e8f0', padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,.04)', display: 'flex', alignItems: 'center', gap: 16, cursor: 'pointer', transition: 'box-shadow .15s' }}
                  onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,.08)'}
                  onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,.04)'}
                >
                  <div style={{ width: 42, height: 42, borderRadius: 10, flexShrink: 0, background: meta.bg, border: '1.5px solid ' + meta.border, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>
                    {draft.status === 'drafting' ? '✏️' : draft.status === 'reviewing' ? '🔍' : draft.status === 'complete' ? '✅' : '📬'}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: '#0f1f3d', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{draft.title || 'Untitled SOW'}</div>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99, color: meta.color, background: meta.bg, border: '1px solid ' + meta.border, whiteSpace: 'nowrap' }}>{meta.label}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 12, color: '#94a3b8' }}>
                      {draft.reference_number && <span style={{ fontFamily: 'monospace', fontSize: 11, background: '#f1f5f9', padding: '2px 6px', borderRadius: 4, color: '#475569' }}>{draft.reference_number}</span>}
                      <span>Updated {fmtRelative(draft.updated_at)}</span>
                      <span>Created {fmtDate(draft.created_at)}</span>
                    </div>
                    {draft.initial_idea && (
                      <div style={{ fontSize: 12, color: '#64748b', marginTop: 5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {draft.initial_idea.slice(0, 120)}{draft.initial_idea.length > 120 ? '…' : ''}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                    <button onClick={() => openDraft(draft)} style={{ padding: '7px 16px', borderRadius: 7, border: '1.5px solid #c49922', background: '#fffbeb', color: '#92400e', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                      {draft.status === 'complete' ? 'Review' : 'Continue →'}
                    </button>
                    <button onClick={() => deleteDraft(draft.id)} disabled={deleting === draft.id} style={{ padding: '7px 10px', borderRadius: 7, border: '1.5px solid #fee2e2', background: '#fff', color: '#dc2626', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                      {deleting === draft.id ? '…' : '✕'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div style={{ textAlign: 'center', marginTop: 40, color: '#cbd5e1', fontSize: 11 }}>
          Powered by Penny · GoldenScope © 2025
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%,100%{opacity:.3} 50%{opacity:1} }
      `}</style>
    </div>
  );
}
